import React, {useState} from 'react';
import {Alert, TabsProps} from 'antd';
import {Button, Card, Flex, Tabs, Tag, Typography, Grid} from 'antd';
import {ArrowLeftOutlined, CalendarOutlined, MailOutlined, PhoneOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import FirstTourTab from './FirstTourTab';
import SecondTourTab from './SecondTourTab';
import ResultsTab from './ResultsTab';
import PersonalInfoTab from './PersonalInfoTab';
import {useLocation} from "react-router";

const {Title, Text} = Typography;
const {useBreakpoint} = Grid;

interface Olympiad {
    id: number;
    subject: string;
    period: string;
    deadline: Date;
    grades: string;
    isRegistered?: boolean;
    olympiadDate?: Date;
    firstTourPass?: boolean | null;
}

const OlympiadDetails: React.FC = () => {
    const [activeTab, setActiveTab] = useState<string>('1');
    const screens = useBreakpoint();
    const navigate = useNavigate();
    const location = useLocation();

    const handleBack = () => {
        navigate(-1); 
    };

    const olympiadData = location.state?.olympiad as Olympiad;

    if (!olympiadData) {
        return (
            <div style={{maxWidth: '1200px', margin: '0 auto', padding: '16px'}}>
                <Alert
                    message="Ошибка"
                    description="Данные об олимпиаде не найдены. Пожалуйста, вернитесь к списку олимпиад и выберите олимпиаду снова."
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => navigate(-1)}>
                            Назад
                        </Button>
                    }
                />
            </div>
        );
    }

    // Данные об олимпиаде
    const olympiadInfo = {
        title: "Межрегиональная отраслевая олимпиада школьников \"Навигатор\"",
        discipline: olympiadData.subject,
        period: olympiadData.period
    };

    // Конфигурация вкладок
    const tabItems: TabsProps['items'] = [
         {
            key: '1',
            label: 'Первый (отборочный) тур',
            children: <FirstTourTab firstTourPass={olympiadData.firstTourPass} />,
        },
        {
            key: '2',
            label: 'Второй (заключительный) тур',
            children: <SecondTourTab />,
            disabled: olympiadData.firstTourPass === null || !olympiadData.firstTourPass
        },
        {
            key: '3',
            label: 'Результаты',
            children: <ResultsTab />,
        },
        {
            key: '4',
            label: 'Информация',
            children: <PersonalInfoTab />,
        },
    ];

    return (
        <div style={{maxWidth: '1200px', margin: '0 auto', padding: screens.xs ? '8px' : '16px', width: '100%'}}>
            {/* Заголовок и кнопка назад */}
            <Flex align="center" gap="middle" style={{marginBottom: screens.xs ? '12px' : '16px'}}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    type="text"
                    size={screens.xs ? "small" : "middle"}
                />
                <Title
                    level={4}
                    style={{
                        margin: 0,
                        flex: 1,
                        fontSize: screens.xs ? '16px' : '18px',
                        cursor: 'pointer'
                    }}
                    onClick={handleBack}
                >
                    Мои олимпиады
                </Title>
            </Flex>

            {/* Основная информация об олимпиаде - компактная версия */}
            <Card
                style={{marginBottom: screens.xs ? '12px' : '16px'}}
                styles={{body: {padding: screens.xs ? '12px' : '16px'}}}
                size="small"
            >
                <Title level={4} style={{
                    marginBottom: screens.xs ? '8px' : '12px',
                    marginTop: 0,
                    color: '#1677ff',
                    fontSize: screens.xs ? '16px' : '18px'
                }}>
                    {olympiadInfo.title}
                </Title>

                <Flex vertical gap="small">
                    <Flex align={screens.xs ? "start" : "center"} gap="small" vertical={screens.xs}>
                        <Text strong style={{minWidth: screens.xs ? 'auto' : '100px'}}>Дисциплина:</Text>
                        <Flex gap="4px" wrap="wrap">
                            {olympiadInfo.discipline.split(', ').map((item, index) => (
                                <Tag key={index} color="blue" style={{marginBottom: screens.xs ? '4px' : 0}}>
                                    {item}
                                </Tag>
                            ))}
                        </Flex>
                    </Flex>

                    <Flex align={screens.xs ? "start" : "center"} gap="small" vertical={screens.xs}>
                        <Text strong style={{minWidth: screens.xs ? 'auto' : '100px'}}>Период:</Text>
                        <Flex align="center" gap="small">
                            <CalendarOutlined style={{fontSize: '12px'}}/>
                            <Text>{olympiadInfo.period}</Text>
                        </Flex>
                    </Flex>

                    <Flex align={screens.xs ? "start" : "center"} gap="small" vertical={screens.xs}>
                        <Text strong style={{minWidth: screens.xs ? 'auto' : '100px'}}>Контакты:</Text>
                        <Flex vertical gap="xs">
                            <Flex align="center" gap="small">
                                <a
                                    href="mailto:fdp@rut-miit.ru "
                                    style={{fontSize: '13px'}}
                                >
                                    <MailOutlined/> fdp@rut-miit.ru 
                                </a>
                            </Flex>
                            <Flex align="center" gap="small">
                                <PhoneOutlined style={{fontSize: '12px'}}/>
                                <Text style={{fontSize: '13px'}}>+7 (495) 260-23-32</Text>
                            </Flex>
                        </Flex>
                    </Flex>

                    {olympiadData.firstTourPass !== null && (
                        <Flex align={screens.xs ? "start" : "center"} gap="small" vertical={screens.xs}>
                            <Text strong style={{minWidth: screens.xs ? 'auto' : '100px'}}>Первый тур:</Text>
                            <Tag color={olympiadData.firstTourPass ? 'green' : 'red'}>
                                {olympiadData.firstTourPass ? 'Пройден' : 'Не пройден'}
                            </Tag>
                        </Flex>
                    )}
                </Flex>
            </Card>

            {/* Вкладки */}
            <Card styles={{body: {padding: screens.xs ? '8px' : '16px'}}}>
                <Tabs
                    activeKey={activeTab}
                    items={tabItems}
                    onChange={setActiveTab}
                    type={screens.xs ? "line" : "card"}
                    size={screens.xs ? "small" : "middle"}
                    tabBarStyle={{padding: screens.xs ? '0 8px' : '0 16px', margin: 0}}
                />
            </Card>
        </div>
    );
};

export default OlympiadDetails;